import { getToken } from 'data/auth/utils'
import { EntityId } from 'entities-next/common'
import { createApiHandler, CreateApiHandlerConfig } from 'services-next/api'
import { ApiCaller, callApi } from 'services/api'
import { getUrlWithPathParams } from 'utils-next/api'
import { strictPick } from 'utils/types'

import { PaginationOptions } from '../../utils-next/types'
import { PagedResult } from '../../utils/pagedResult'
import {
  ApiCreateScoutingCampaignData,
  PatchScoutingCampaign,
  ScoutingCampaign,
  ScoutingCampaignStatus,
} from './scoutingCampaign.types'

const SCOUTING_CAMPAIGN_ENDPOINT_PATH = 'admin/scouting-campaign/'

export type ScoutingCampaignListFilters = {
  ids?: EntityId[]
  corporateId?: EntityId
  search?: string
  status?: ScoutingCampaignStatus
}
export function getApiScoutingCampaignHandlers(
  customToken?: string,
  config: Omit<CreateApiHandlerConfig, 'baseUrl'> = {}
) {
  const token = customToken || getToken() || undefined
  const { callApi } = createApiHandler(token, config)

  async function apiGetScoutingCampaign(id: EntityId): Promise<ScoutingCampaign> {
    return callApi<ScoutingCampaign>({
      method: 'GET',
      url: getUrlWithPathParams(SCOUTING_CAMPAIGN_ENDPOINT_PATH, id),
    })
  }

  async function apiCreateDemoSpaceForCorporate(corporateId: EntityId): Promise<ScoutingCampaign> {
    return callApi<ScoutingCampaign>({
      method: 'POST',
      url: SCOUTING_CAMPAIGN_ENDPOINT_PATH + 'create/demo-space',
      data: {
        corporateId: corporateId,
      },
    })
  }

  async function apiGetScoutingCampaignList(
    filters: ScoutingCampaignListFilters,
    paginationOptions: PaginationOptions
  ): Promise<PagedResult<ScoutingCampaign>> {
    return await callApi<PagedResult<ScoutingCampaign>>({
      url: SCOUTING_CAMPAIGN_ENDPOINT_PATH,
      params: {
        corporateId: filters.corporateId,
        search: filters.search,
        status: filters.status ? [filters.status] : [],
        'ids[]': filters.ids,
        page: paginationOptions.currentPage + 1,
        limit: paginationOptions.limit,
      },
    })
  }

  async function apiCreateScoutingCampaign(data: ApiCreateScoutingCampaignData): Promise<ScoutingCampaign> {
    return callApi<ScoutingCampaign>({
      method: 'POST',
      url: SCOUTING_CAMPAIGN_ENDPOINT_PATH,
      data: {
        ...data,
      },
    })
  }

  async function apiDeleteScoutingCampaign(id: EntityId): Promise<ScoutingCampaign> {
    return callApi<ScoutingCampaign>({
      method: 'DELETE',
      url: getUrlWithPathParams(SCOUTING_CAMPAIGN_ENDPOINT_PATH, id),
    })
  }

  return {
    apiGetScoutingCampaign,
    apiGetScoutingCampaignList,
    apiCreateScoutingCampaign,
    apiDeleteScoutingCampaign,
    apiCreateDemoSpaceForCorporate,
  }
}

const apiUpdateScoutingCampaign = (apiCaller: ApiCaller) => async (
  scoutingCampaignId: number,
  updateScoutingCampaign: PatchScoutingCampaign
): Promise<ScoutingCampaign> => {
  const keysToUpdate: (keyof PatchScoutingCampaign)[] = [
    'name',
    'description',
    'status',
    'editable',
    'positiveKeywords',
    'negativeKeywords',
    'type',
    'assignedOpsLeadId',
    'internalPositivePrimaryKeywordsSets',
    'internalPositiveSecondaryKeywords',
    'internalNegativePrimaryKeywords',
    'internalNegativeSecondaryKeywords',
    'internalLocations',
    'internalNegativeLocations',
    'internalStages',
    'internalMinimumYear',
    'internalMaximumYear',
    'internalIncludeCompaniesWithoutMaturityInformation',
    'packageCountable',
    'dailyCompanyLimit',
    'hoursBetweenBatchesOfPairings',
    'radarScouting',
    'relaxedSweetSpotDefinition',
    'includeCompaniesWithoutMaturityInformation',
    'isDeleted',
  ]

  const updateScoutingCampaignUpdate = strictPick(updateScoutingCampaign, keysToUpdate)

  return apiCaller<ScoutingCampaign>({
    method: 'PATCH',
    url: getUrlWithPathParams(SCOUTING_CAMPAIGN_ENDPOINT_PATH, scoutingCampaignId),
    data: updateScoutingCampaignUpdate,
  })
}

export const apiUpdateScoutingCampaignAsAdmin = apiUpdateScoutingCampaign(callApi)
